// Validation utilities for react-hook-form
// Usage: {...register("email", emailRule())}
// Or dynamic ID: {...register("idNo", getGovIdValidation(watch("idType")))}

// Common regex patterns (mostly India-centric defaults; adjust as needed)
export const patterns = {
  email: /^[\w.!#$%&'*+/=?^`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/, // RFC 5322-light
  // Indian mobile: 10 digits starting 6-9. If you want any 10 digits: /^\d{10}$/
  phoneIN: /^[6-9]\d{9}$/,
  // International (E.164-like): optional +, 7 to 15 digits, cannot start with 0
  phoneIntl: /^\+?[1-9]\d{6,14}$/,
  // Indian PIN code: 6 digits, first not 0
  pincodeIN: /^[1-9]\d{5}$/,
  // Name: letters with spaces and common punctuation, 2-50 chars
  name: /^[A-Za-z\s.'-]*[A-Za-z][A-Za-z\s.'-]*$/,
};

// Rule builders for react-hook-form "register"
export const emailRule = (customMessage = 'Invalid email address') => ({
  required: 'Required',
  pattern: { value: patterns.email, message: customMessage },
});

export const phoneRule = ({ required = true, message = 'Invalid phone number', allowAny10Digits = false } = {}) => {
  const value = allowAny10Digits ? /^\d{10}$/ : patterns.phoneIN;
  return {
    ...(required ? { required: 'Required' } : {}),
    pattern: { value, message },
  };
};

// International phone rule (E.164-like)
export const phoneIntlRule = ({ required = true, message = 'Enter a valid phone in international format (e.g., +14155552671)' } = {}) => ({
  ...(required ? { required: 'Required' } : {}),
  pattern: { value: patterns.phoneIntl, message },
});

// Helper to choose phone rule by mode: 'in' | 'intl'
export const getPhoneRule = (mode = 'in', options = {}) => {
  return mode === 'intl' ? phoneIntlRule(options) : phoneRule(options);
};

export const pincodeRule = ({ required = false, message = 'Invalid PIN code' } = {}) => ({
  ...(required ? { required: 'Required' } : {}),
  pattern: { value: patterns.pincodeIN, message },
});

export const nameRule = ({ required = true, min = 1, max = 50, message = "Only alphabets, spaces and .'- are allowed" } = {}) => ({
  ...(required ? { required: 'Required' } : {}),
  minLength: { value: min, message: `Must be at least ${min} characters` },
  maxLength: { value: max, message: `Must be at most ${max} characters` },
  pattern: { value: patterns.name, message },
});

// Address rule with default max 250 chars
export const addressRule = ({ required = true, max = 250 } = {}) => ({
  ...(required ? { required: 'Required' } : {}),
  maxLength: { value: max, message: `Must be at most ${max} characters` },
});

// Government ID validators (India)
// Add/adjust as your product requires
const govIdValidators = {
  AADHAAR: {
    label: 'Aadhaar',
    pattern: /^\d{12}$/,
    message: 'Enter 12 digit Aadhaar number',
  },
  PAN: {
    label: 'PAN',
    pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    message: 'Enter valid PAN (e.g., ABCDE1234F)',
  },
  PASSPORT: {
    label: 'Passport',
    // Simplified Indian passport pattern (series can vary)
    pattern: /^[A-PR-WY][1-9]\d{6}$/i,
    message: 'Enter valid Passport number',
  },
  DRIVING_LICENSE: {
    label: 'Driving License',
    // DL formats vary by state; this is a common simplified pattern: 2 letters state + 2 digits RTO + 11 digits
    pattern: /^[A-Z]{2}\d{2}\d{11}$/i,
    message: 'Enter valid Driving License number',
  },
  VOTER_ID: {
    label: 'Voter ID',
    pattern: /^[A-Z]{3}\d{7}$/i,
    message: 'Enter valid Voter ID (e.g., ABC1234567)',
  },
  RATION_CARD: {
    label: 'Ration Card',
    pattern: /^[A-Z]{3}\d{7}$/i,
    message: 'Enter valid Ration Card number',
  },
};

// Map your app's idType values to one of the keys above
// Example idType values: 'aadhar', 'pan', 'passport', 'dl', 'voter'
const idTypeMap = {
  aadhaar: 'AADHAAR',
  aadhar: 'AADHAAR',
  pan: 'PAN',
  passport: 'PASSPORT',
  dl: 'DRIVING_LICENSE',
  driving_license: 'DRIVING_LICENSE',
  voter: 'VOTER_ID',
  voter_id: 'VOTER_ID',
  ration_card: 'RATION_CARD',
};

export const getGovIdValidation = (idType, { required = true } = {}) => {
  const key = idTypeMap[(idType?.value || idType || '').toString().toLowerCase()];
  const rule = key ? govIdValidators[key] : null;

  if (!rule) {
    // Fallback generic rule when idType is unknown
    return {
      ...(required ? { required: 'Required' } : {}),
      minLength: { value: 4, message: 'ID seems too short' },
    };
  }
  return {
    ...(required ? { required: 'Required' } : {}),
    pattern: { value: rule.pattern, message: rule.message },
  };
};

// Helper that returns a rule set by type string
export const ruleFor = (type, options) => {
  switch ((type || '').toLowerCase()) {
    case 'email':
      return emailRule(options?.message);
    case 'phone':
      return getPhoneRule('in', options);
    case 'phone_intl':
      return getPhoneRule('intl', options);
    case 'pincode':
      return pincodeRule(options);
    case 'name':
      return nameRule(options);
    case 'address':
      return addressRule(options);
    default:
      return { required: 'Required' };
  }
};
